import { Link } from 'react-router-dom'
import { Github, Linkedin, Twitter, Youtube, Mail, MapPin, Phone, Cookie } from 'lucide-react'
import Logo from './Logo'
import { navigationData } from '../utils/navigation'
import { useCookieConsent } from '../context/CookieConsentContext'

// Footer columns are chosen explicitly so adding a header nav section
// doesn't silently push another one out.
const footerSections = ['products', 'aiOps', 'services', 'support', 'corporate']

const socialLinks = [
  { name: 'X.com', icon: Twitter, href: 'https://x.com/synthetiqio' },
  { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/synthetiq' },
  { name: 'GitHub', icon: Github, href: 'https://github.com/synthetiqio' },
  { name: 'YouTube', icon: Youtube, href: 'https://www.youtube.com/@SynthetIQ-Graffiti' },
]

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { openPreferences } = useCookieConsent()
  
  return (
    <footer className="bg-void-950 border-t border-void-800/50">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <Logo className="h-10 w-10" />
              <div>
                <span className="font-display font-bold text-xl text-void-50 block">
                  SynthetIQ Signals&reg;
                </span>
                <span className="text-xs text-void-500">Operated by SynthetIQ Signals®, Inc.</span>
              </div>
            </Link>
            <p className="mt-4 text-void-400 text-sm leading-relaxed max-w-sm">
              Building trust in the Singularity, one device at a time. Graffiti™ OS, governed by
              23Controls™, and the Scrambler hardware that runs it — supported end to end by
              SynthetIQ Signals®.
            </p>

            {/* Contact Info */}
            <div className="mt-6 space-y-3">
              <a href="mailto:contact@synthetiq.io" className="flex items-center gap-3 text-void-400 hover:text-synth-400 transition-colors text-sm">
                <Mail className="w-4 h-4" />
                contact@synthetiq.io
              </a>
              <a href="tel:+15702095882" className="flex items-center gap-3 text-void-400 hover:text-synth-400 transition-colors text-sm">
                <Phone className="w-4 h-4" />
                +1 570 209 5882
              </a>
              <div className="flex items-center gap-3 text-void-400 text-sm">
                <MapPin className="w-4 h-4" />
                Scranton, PA, USA
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center gap-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-void-800/50 text-void-400 hover:text-synth-400 hover:bg-void-700/50 transition-all"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
          
          {/* Navigation Links */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-5 gap-8">
            {footerSections.map((key) => [key, navigationData[key]]).map(([key, section]) => (
              <div key={key}>
                <h3 className="font-display font-semibold text-void-100 mb-4">
                  {section.title}
                </h3>
                <ul className="space-y-2.5">
                  {section.categories.slice(0, 3).map((category) => (
                    <li key={category.name}>
                      <Link
                        to={category.href}
                        className="text-sm text-void-400 hover:text-synth-400 transition-colors"
                      >
                        {category.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-void-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-void-500">
              © {currentYear} SynthetIQ Signals®, Inc. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6">
              <Link to="/corporate/legal/terms" className="text-sm text-void-500 hover:text-void-300 transition-colors">
                Terms
              </Link>
              <Link to="/corporate/legal/privacy" className="text-sm text-void-500 hover:text-void-300 transition-colors">
                Privacy
              </Link>
              <Link to="/corporate/legal/compliance" className="text-sm text-void-500 hover:text-void-300 transition-colors">
                Compliance
              </Link>
              <button
                onClick={openPreferences}
                className="text-sm text-void-500 hover:text-void-300 transition-colors flex items-center gap-1.5"
              >
                <Cookie className="w-4 h-4" />
                Cookies
              </button>
              <Link to="/support/help/status" className="text-sm text-void-500 hover:text-void-300 transition-colors flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Status
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}